// Themes: a color theme, which sets the accent and the sidebar's colors,
// and light or dark. Both are the account's, set on every device; a person
// who has not chosen a theme has the workspace's. What was last used is
// kept in this browser too, so the page starts in it before signing in.
// applyTheme sets data-mode on <html> to light or dark, and the theme's
// colors inline, which win over tokens.css.

export type Appearance = "" | "light" | "dark";

// Colors are what a theme sets in one mode: the accent (buttons, links,
// the selected channel) and the sidebar's background and text.
export interface Colors {
  accent: string;
  side: string;
  sideFg: string;
}

export type ThemeID = "lantern" | "aubergine" | "forest" | "ocean" | "ember" | "graphite";

// themes are Lantern's color themes, the same ids as the server's. Each
// keeps white text on its accent and its sidebar's text at least 4.5:1
// (theme.test.ts).
export const themes: Record<ThemeID, { light: Colors; dark: Colors }> = {
  lantern: { light: { accent: "#2463eb", side: "#1f2a36", sideFg: "#d7dde4" }, dark: { accent: "#2463eb", side: "#121417", sideFg: "#d7dde4" } },
  aubergine: { light: { accent: "#1164a3", side: "#3f0e40", sideFg: "#e6d6e7" }, dark: { accent: "#1164a3", side: "#2a0a2b", sideFg: "#e6d6e7" } },
  forest: { light: { accent: "#1d7148", side: "#16382a", sideFg: "#d3e5da" }, dark: { accent: "#1d7148", side: "#0e231a", sideFg: "#d3e5da" } },
  ocean: { light: { accent: "#0a6a93", side: "#0b3246", sideFg: "#d2e5ee" }, dark: { accent: "#0a6a93", side: "#071f2c", sideFg: "#d2e5ee" } },
  ember: { light: { accent: "#b93c0b", side: "#3b1d11", sideFg: "#f0dccf" }, dark: { accent: "#b93c0b", side: "#23110a", sideFg: "#f0dccf" } },
  graphite: { light: { accent: "#4b5563", side: "#25282d", sideFg: "#d8dbe0" }, dark: { accent: "#5d6776", side: "#17191c", sideFg: "#d8dbe0" } },
};

export const themeIDs = Object.keys(themes) as ThemeID[];

const isTheme = (v: unknown): v is ThemeID => typeof v === "string" && v in themes;

const modeKey = "lantern.theme";
const themeKey = "lantern.color-theme";
const syncedKey = "lantern.theme-synced";

const read = (k: string) => {
  try {
    return localStorage.getItem(k) ?? "";
  } catch {
    return "";
  }
};
const write = (k: string, v: string) => {
  try {
    if (v) localStorage.setItem(k, v);
    else localStorage.removeItem(k);
  } catch {
    // It still applies until the page reloads.
  }
};

// The account's choices, once signed in, and the workspace's theme and
// accent (its branding's).
let own: { theme: string; appearance: Appearance } | null = null;
let workspaceTheme = "";
let workspaceAccent = "";

// appearance is light or dark as chosen, or "" for the system's.
export function appearance(): Appearance {
  const v = own ? own.appearance : read(modeKey);
  return v === "light" || v === "dark" ? v : "";
}

// ownTheme is the theme chosen, or "" for the workspace's.
export function ownTheme(): string {
  return own?.theme ?? "";
}

// workspaceThemeID is the theme of those who have not chosen one.
export function workspaceThemeID(): ThemeID {
  return isTheme(workspaceTheme) ? workspaceTheme : "lantern";
}

// themeInUse is the theme shown: one's own, else the workspace's.
export function themeInUse(): ThemeID {
  if (!own) {
    const cached = read(themeKey);
    return isTheme(cached) ? cached : "lantern";
  }
  return isTheme(own.theme) ? own.theme : workspaceThemeID();
}

// fromAccount takes the account's theme and appearance, as the server has
// them. A mode chosen in this browser before themes were the account's is
// sent up once, so it is not lost; it reports whether one was.
export function fromAccount(p: { theme?: string; appearance?: string }, save: (theme: string, appearance: Appearance) => void): boolean {
  const a: Appearance = p.appearance === "light" || p.appearance === "dark" ? p.appearance : "";
  const local = read(modeKey);
  const migrate = !a && (local === "light" || local === "dark") && !read(syncedKey);
  write(syncedKey, "1");
  own = { theme: p.theme ?? "", appearance: migrate ? (local as Appearance) : a };
  if (migrate) save(own.theme, own.appearance);
  applyTheme();
  return migrate;
}

export function fromWorkspace(theme: string | undefined) {
  workspaceTheme = theme ?? "";
  applyTheme();
}

// fromBrand takes the branding's accent, which those who have not chosen a
// theme of their own see in place of the theme's.
export function fromBrand(accent: string) {
  workspaceAccent = accent;
  applyTheme();
}

// choose shows a theme and appearance at once, before the server has them.
export function choose(theme: string, a: Appearance) {
  own = { theme, appearance: a };
  applyTheme();
}

export function applyTheme() {
  const root = document.documentElement;
  const a = appearance();
  const mode = a || (system?.matches ? "dark" : "light");
  const id = themeInUse();
  write(modeKey, a);
  write(themeKey, id);
  if (a) root.dataset.theme = a;
  else delete root.dataset.theme;
  root.dataset.mode = mode;
  root.dataset.colorTheme = id;
  const c = themes[id][mode];
  root.style.setProperty("--accent", workspaceAccent && !own?.theme ? workspaceAccent : c.accent);
  root.style.setProperty("--side-bg", c.side);
  root.style.setProperty("--side-fg", c.sideFg);
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", c.side);
}

const system = typeof matchMedia === "function" ? matchMedia("(prefers-color-scheme: dark)") : undefined;
system?.addEventListener("change", () => applyTheme());
